import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  SummaryPageContainer,
  SummaryCard,
  SummaryHeader,
  SummaryEyebrow,
  SummaryTitle,
  SummaryIntro,
  SummaryIntroText,
  SummaryBody,
  SummarySection,
  SectionTitle,
  PricePill,
  BulletList,
  Note,
  CTABox,
  CTAHeading,
  ButtonRow,
  CTAButton,
  SecondaryButton,
  NoteTitle,
  FallbackBox,
  OptionalText
} from "./SummaryElements"

export const PACKAGES = {
  // Wedding (silver/gold/diamond)
  WEDDING_SILVER: "wedding_silver",
  WEDDING_GOLD: "wedding_gold",
  WEDDING_DIAMOND: "wedding_diamond",
  // Event
  EVENT_BASIC: "event_basic",
  EVENT_STANDARD: "event_standard",
  EVENT_PREMIUM: "event_premium",
  // Promotional
  PROMO_BASIC: "promotional_basic",
  PROMO_STANDARD: "promotional_standard",
  PROMO_PREMIUM: "promotional_premium",
  // Social Media
  SOCIAL_BASIC: "social_basic",
  SOCIAL_STANDARD: "social_standard",
  SOCIAL_PREMIUM: "social_premium",
};

const CONTENT = {
  [PACKAGES.WEDDING_SILVER]: {
    label: "🥈 SILVER PACKAGE",
    price: "£899 (+ travel costs)",
    title: "Here's everything you need to know about your selected package:",
    subtitle: "Includes:",
    bullets: [
        "1-Day Filming + Post-production",
        "Highlight Video (3-4 minutes) - with music, effects, colour correction & grading",
        "1x Social Media Reel - optimised for Instagram, Facebook & TikTok (vertical format)",
        "Drone Footage Included (weather and location permitting)",
        "Extra Location Shoot - to add depth and creative flair",
        "Creative Visual Effects & Unique Templates - tailored for a cinematic feel",
        "Professional Contract - clear terms, guaranteed security and quality",
        "Pre-wedding Idea Meeting - via video, phone or text",
        "Unlimited Downloads -  delivered in 4K and HD",
    ],
    note: "All packages include cinematic filming from morning preparations through to the end of the first dance. I always stay a little longer to capture additional moments on the dancefloor — those spontaneous, joyful shots that complete the story."
  },
  [PACKAGES.WEDDING_GOLD]: {
    label: "🥇 GOLD PACKAGE",
    price: "£1,250 (+ travel costs)",
    title: "Here's everything you need to know about your selected package:",
    subtitle: "Includes:",
    bullets: [
        "Wedding Reportage (10-15 minutes) - storytelling-style, documentary approach",
        "Highlight Video (3-4 minutes) - music, colour correction & grading included",
        "1x Social Media Reel - optimised for Instagram, Facebook & TikTok (vertical format)",
        "1-Day Filming + Full Post-production",
        "Drone Footage Included (weather and location permitting)",
        "Extra Location Shoot - to add depth and creative flair",
        "Creative Visual Effects & Unique Templates - tailored for a cinematic feel",
        "Professional Contract - clear terms, guaranteed security and quality",
        "Pre-wedding Idea Meeting - via video, phone or text",
        "Unlimited Downloads - delivered in 4K and HD",
    ],
    note: "All packages include cinematic filming from morning preparations through to the end of the first dance. I always stay a little longer to capture additional moments on the dancefloor — those spontaneous, joyful shots that complete the story."
  },
  [PACKAGES.WEDDING_DIAMOND]: {
    label: "💎 DIAMOND PACKAGE",
    price: "£2,650 (including travel)",
    title: "Here's everything you need to know about your selected package:",
    subtitle: "Includes:",
    bullets: [
        "2-Person Film Crew - for full, multi-angle cinematic coverage",
        "1-Day Filming + Full Post-production",
        "Wedding Reportage (15-30 minutes) - storytelling-style, documentary approach",
        "Highlight Video (3-4 minutes) - fully edited with music, colour correction & final grading",
        "2x Social Media Reels - optimised for Instagram, Facebook & TikTok (vertical format)",
        "Creative Visual Effects & Unique Templates - tailored for a cinematic feel",
        "Drone Footage Included (weather and location permitting)",
        "Extra Location Shoot - to add depth and creative flair",
        "1 Round of Revision - your input matters",
        "Professional Contract - clear terms, guaranteed security and quality",
        "Pre-wedding Idea Meeting - via video, phone or text",
        "Unlimited Downloads - delivered in 4K and HD",
    ],
    note: "All packages include cinematic filming from morning preparations through to the end of the first dance. I always stay a little longer to capture additional moments on the dancefloor — those spontaneous, joyful shots that complete the story."
  },
  // Event
  [PACKAGES.EVENT_BASIC]: {
    label: "⭐ Basic Package",
    price: "£599",
    title: "Perfect for small events, networking meetups or community gatherings",
    subtitle: "Includes:",
    noListStyle: true,
    bullets: [
        "✔ 4h Event Coverage",
        "✔ 1 Highlight Video (1-2 minutes, horizontal 16:9)",
        "✔ Short Teaser Clip (vertical 9:16, perfect for Stories/Reels)",
        "✔ Drone Footage (where permitted)",
        "✔ Premium Editing (colour correction & grading, dynamic pacing, transitions & effects)",
        "✔ Licensed Commercial Music - no copyright issues",
        "✔ Online Delivery Within 14 days",
        "Extras (add-ons)",
        "🎥  Extra Camera Operator - £400/day",
        "⏱  24h Highlight Turnaround - £300",
        "🎬  Full Event Recording with Chapter Markers - from £500",
        "🎤 Guest/Speaker Interview Shots - £100",
    ],
    note: "👉 A simple but powerful way to capture your event and share it online."
  },
  [PACKAGES.EVENT_STANDARD]: {
    label: "⭐⭐ Standard Package",
    price: "£1199",
    title: "Ideal for business events, corporate functions or brand activations.",
    subtitle: "Includes:",
    noListStyle: true,
    bullets: [
        "✔ 6-8h Event Coverage",
        "✔ 1 Highlight Film (3-4 minutes, horizontal 16:9)",
        "✔ 3 Social Media Clips:",
        "• 2x Vertical (9:16) - TikTok, Instagram Reels, YouTube Shorts, Facebook",
        "• 1x Horizontal (16:9) - LinkedIn & YouTube",
        "✔ Drone Footage (where permitted)",
        "✔ Premium Editing (colour correction & grading, dynamic pacing, transitions & effects)",
        "✔ Guest/Speaker Interview shots included",
        "✔ Licensed Commercial Music - no copyright issues",
        "✔ Online Delivery Within 10-14 Days",
        "Extras (add-ons)",
        "🎥  Extra Camera Operator - £400/day",
        "⏱  24h Highlight Turnaround - £300",
        "🎬  Full Event Recording with Chapter Markers - from £500",
        <span>🎤 Guest/Speaker Interview Shots <span style={{color: 'yellow'}}>INCLUDED</span></span>,
    ],
    note: "👉 The perfect balance of professional coverage and ready-to-use marketing content."
  },
  [PACKAGES.EVENT_PREMIUM]: {
    label: "⭐⭐⭐ Premium Package",
    price: "£1999",
    title: "Designed for conferences, large-scale events and high-profile productions.",
    subtitle: "Includes:",
    noListStyle: true,
    bullets: [
        "✔ Full-Day Coverage (up to 10 hours)",
        "✔ 2 Camera Angles + Drone Footage (where permitted)",
        "✔ 1 Full Event Film (4-5 minutes, horizontal 16:9)",
        "✔ 5 Social Media Edits:",
        "• 2x Vertical (9:16) - Reels/TikTok/Stories",
        "• 1x Square (1:1) - Instagram & Facebook",
        "• 2x Horizontal (16:9) - LinkedIn, YouTube, website",
        "✔ 24h Delivery - Teaser Video (30 sec, vertical 9:16)",
        "✔ Premium Editing (colour correction & grading, dynamic pacing, transitions & effects)",
        "✔ Guest/Speaker Interview shots included",
        "✔ Branded Landing Page with all event media",
        "✔ Licensed Commercial Music - no copyright issues",
        "Extras (add-ons)",
        "🎥  Extra Camera Operator - £400/day",
        <span>⏱  24h Highlight <span style={{color: 'yellow'}}>INCLUDED</span></span>,
        <span>🎬  Full Event Recording with Chapter <span style={{color: 'yellow'}}>INCLUDED</span></span>,
        <span>🎤 Guest/Speaker Interview Shots <span style={{color: 'yellow'}}>INCLUDED</span></span>,
    ],
    note: "👉 Comprehensive coverage and a full set of marketing assets - everything you need to showcase your event across every platform."
  },
  // Promotional
  [PACKAGES.PROMO_BASIC]: {
    label: "📽 Essential Promo",
    price: "£790",
    title: "For businesses seeking a professional video that effectively sells.\nPerfect for websites, YouTube, and presentations.",
    subtitle: "Includes:",
    bullets: [
        "1 Full Filming Day (4-8 hours) - capturing interiors, product/service details, your team at work, and interactions with clients.",
        "Promotional Film (2-3 minutes) - pace and style tailored to your industry and sales strategy.",
        "Professional Lighting, Microphones, and Drone Footage.",
        "Full Script and Professional Contract - clear collaboration terms and a structured production plan.",
        "Premium Editing - colour correction & grading, seamless transitions, captions, and dynamic pacing.",
        "Audio Optimisation - noise reduction, level balancing, and audio mastering.",
        "Licensed Commercial Music - no copyright issues.",
        "1 Round of Revision",
        "Full Commercial Rights.",
        "12-month Footage Archiving.",
    ],
    noteTitle: "💡 Why it's worth it:",
    note: "A one-time production that becomes your long-lasting video business card, working for you both online and offline for years."
  },
  [PACKAGES.PROMO_STANDARD]: {
    label: "📲 Cinematic + Social Boost",
    price: "£1390",
    title: "For businesses that want a professional promotional film plus ready-made social media content to maximise visibility.",
    subtitle: "Includes:",
    bullets: [
        "1 Full Filming Day (4-8 hours) - capturing interiors, product/service details, your team at work, and interactions with clients.",
        "Promotional Film (2-3 minutes) - pace and style tailored to your industry and sales strategy.",
        "Professional Lighting, Microphones, and Drone Footage.",
        "Full Script and Professional Contract - clear collaboration terms and a structured production plan.",
        "Premium Editing - colour correction & grading, seamless transitions, captions, and dynamic pacing.",
        "Audio Optimisation - noise reduction, level balancing, and audio mastering.",
        "Licensed Commercial Music - no copyright issues.",
        "1 Round of Revision",
        "Full Commercial Rights.",
        "12-month Footage Archiving.",
        "3 Vertical Social Media Reels (15-30 seconds).",
        "1 Short Cut-down of The Main Film (15-30 seconds) - in both horizontal and vertical formats, perfect for paid ads and quick promotions.",
        "Trend Research - tailored reel concepts optimised for current algorithms.",
        "Mini Photo Pack (10 images) - behind-the-scenes shots or product/service details.",
    ],
    noteTitle: "💡 Why it's worth it:",
    note: "You get a complete video campaign - the main cinematic film builds your brand image, while the reels and images drive reach and engagement."
  },
  [PACKAGES.PROMO_PREMIUM]: {
    label: "🚀 Full Brand Video Suite",
    price: "£2,690",
    title: "For brands ready to dominate social media and own a complete video toolkit for all communication channels.",
    subtitle: "Includes:",
    bullets: [
        "1 Full Filming Day (4-8 hours) - capturing interiors, product/service details, your team at work, and interactions with clients.",
        "Promotional Film (2-3 minutes) - pace and style tailored to your industry and sales strategy.",
        "Professional Lighting, Microphones, and Drone Footage.",
        "Full Script and Professional Contract - clear collaboration terms and a structured production plan.",
        "Premium Editing - colour correction & grading, seamless transitions, captions, and dynamic pacing.",
        "Audio Optimisation - noise reduction, level balancing, and audio mastering.",
        "Licensed Commercial Music - no copyright issues.",
        "Full Commercial Rights.",
        "12-month Footage Archiving.",
        "3 Vertical Social Media Reels (15-30 seconds).",
        "1 Short Cut-down of The Main Film (15-30 seconds) - in both horizontal and vertical formats, perfect for paid ads and quick promotions.",
        "Trend Research - tailored reel concepts optimised for current algorithms.",
        "Mini Photo Pack (10 images) - behind-the-scenes shots or product/service details.",
        "6 Vertical Social Media Reels (15-30 seconds, 9:16 format) - varied topics and angles, giving you weeks of ready-to-post content.",
        "BTS (Behind The Scenes) Video (30-60 seconds) - showcasing your process, team, and brand personality, ideal for building trust and connection.",
        "1-Month Content Strategy - topics, calls-to-action, and posting recommendations.",
        "Loop Background Video (15-20 seconds) - cinematic motion loop for website headers, in-store screens, or events.",
        "2-3 Short Cut-downs of The Main Film (15-30 seconds) - in both horizontal and vertical formats, perfect for paid ads and quick promotions.",
        "2 Rounds of Revisions",
    ],
    noteTitle: "💡 Why it's worth it:",
    note: " A full suite of high-quality, versatile video assets that let you run consistent, professional, and effective online marketing - with no need for extra shoots for months."
  },
  // Social Media
  [PACKAGES.SOCIAL_BASIC]: {
    label: "Social Starter",
    price: "£399/month",
    title: "For brands that want to stand out with professional video and instantly grab attention on social media.",
    subtitle: "Each month you get:",
    bullets: [
        "1-Day of Filming at your location (up to 4h on set)",
        "2 Fully Edited Reels (15-30 seconds, vertical 9:16 format)",
        "Creative Consultation - to tailor the content to your business goals",
        "Professional Lighting, Microphone, and Drone Shots",
        "Premium Editing: transitions, speed ramps, text overlays, colour correction & grading",
        "Audio Optimisation: noise reduction, level balancing, audio mastering",
        "Licensed Royalty-free Music safe for all platforms",
        "Footage Archived for 3 months",
        
    ],
    optional: "➕ Optional: trending library music for maximum reach £30 each",
    note: "Why it works: A quick and effective way to launch a professional social media presence that makes your brand stand out from day one. Perfect for creating instant impact and grabbing your audience's attention."
  },
  [PACKAGES.SOCIAL_STANDARD]: {
    label: "Growth Pro",
    price: "£699/month",
    title: "For brands that want consistent, regular, and professional video content to keep their audience engaged.",
    subtitle: "Each month you get:",
    bullets: [
        "1-Day of Filming at your location (up to 6h on set)",
        "4 Fully Edited Reels (15-30 seconds, vertical 9:16 format)",
        "Creative Consultation to align content with your business objectives",
        "Professional Lighting, Microphone, and Drone Shots",
        "Premium Editing: transitions, speed ramps, text overlays, colour correction & grading",
        "Audio Optimisation: noise reduction, level balancing, audio mastering",
        "Licensed Royalty-free Music safe for all platforms",
        "Priority Delivery with 2 Rounds of Revisions Included",
        "Footage Archived for 6 months",
    ],
    optional: "➕ Optional: trending library music for maximum reach £30 each",
    note: "Why it works: Keeps your brand visible and relevant without downtime. Four high-quality Reels every month ensure your audience remembers you, helping convert followers into loyal customers."
  },
  [PACKAGES.SOCIAL_PREMIUM]: {
    label: "Brand Dominator",
    price: "£1,299/month",
    title: "For brands ready to dominate their industry and publish content that attracts, engages, and converts.",
    subtitle: "Each month you get:",
    bullets: [
        "2-Days of Filming (up to 6h per day, can be split into smaller sessions)",
        "8 Fully Edited Reels (15-30 seconds, vertical 9:16 format)",
        "A Full Monthly Content Strategy: topics, formats, scripts, and calls-to-action",
        "Professional Lighting, Microphone, and Drone Shots",
        "Dedicated Trend Research to ensure your videos align with current algorithms",
        "Premium Editing: transitions, speed ramps, text overlays, colour correction & grading",
        "Cinematic-grade colour grading to match your brand style",
        "Audio Optimisation: noise reduction, level balancing, audio mastering",
        "Licensed Royalty-free Music safe for all platforms",
        "3 Rounds of Revisions Included",
        "Full Commercial Rights for Publishing",
        "Footage Archived for 12 months",
        "Monthly Performance Report with Recommendations for Optimisation",
    ],
    optional: "➕ Optional: trending library music for maximum reach £30 each",
    note: "Why it works: Positions your brand as a leader in your market. With 8 perfectly crafted, trend-driven Reels each month, you'll stay top of mind for your audience and outshine your competitors, driving more visibility, engagement, and revenue"
  },
};

function TitleFromKey(key) {
  if (!key) return "Selected Package";
  const item = CONTENT[key];
  if (!item) return "Selected Package";
  return `${item.label} `;
}

export default function Summary() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const typeParam = params.get("type");

  const content = CONTENT[typeParam ?? ""];

  return (
    <SummaryPageContainer>
      <SummaryCard>
        <SummaryHeader>
          <SummaryEyebrow>Great choice! 🎉</SummaryEyebrow>
          <SummaryTitle>
            {TitleFromKey(typeParam)}
            {content?.price && <PricePill>{content.price}</PricePill>}
          </SummaryTitle>
          <SummaryIntro>
            {content?.title}
          </SummaryIntro>
        </SummaryHeader>

        <SummaryBody>
          <SummarySection>
            {content ? (
              <>
                <SectionTitle>{content.subtitle}</SectionTitle>
                <BulletList style={{ listStyle: content.noListStyle ? "none" : "disc", paddingLeft: content.noListStyle ? "0" : "18px" }}>
                {content.bullets.map((b) => (
                    <li key={b}>{b}</li>
                ))}
                </BulletList>
                <OptionalText>
                    {content.optional}
                </OptionalText>
                <NoteTitle>
                    {content.noteTitle}
                </NoteTitle>
                <Note>
                    {content.note}
                </Note>
              </>
            ) : (
              <FallbackBox>
                Can't find that package. Please select a plan from our
                <br />
                <br />
                <SecondaryButton onClick={() => navigate("/wedding")}>Wedding</SecondaryButton>{" "}
                <SecondaryButton onClick={() => navigate("/event")}>Event</SecondaryButton>{" "}
                <SecondaryButton onClick={() => navigate("/promotional")}>Promotional</SecondaryButton>{" "}
                <SecondaryButton onClick={() => navigate("/social-media")}>Social Media</SecondaryButton>
              </FallbackBox>
            )}
          </SummarySection>

          <SummarySection>
            <CTABox>
              <CTAHeading>Let's make it happen 🌟</CTAHeading>
              <SummaryIntroText>
                <br/>
                Please share the details so we can schedule the filming date.
                <br/>
                <br/>
                Tell us about your vision or project.
                <br/>
                We're here to bring your ideas to life.
                <br/>
                <br/>
                You can reach us by call, email, or through
                <br/> 
                our quick form — whatever works best for you.
                <br/> 
                <br/> 
              </SummaryIntroText>
              <ButtonRow>
                <CTAButton href="tel:+447736584565" aria-label="Call">
                  📞 Call
                </CTAButton>
                <CTAButton href="mailto:progressstudio.info@gmail.com" aria-label="Email">
                  ✉️ Email
                </CTAButton>
                <CTAButton as="button" onClick={() => navigate("/contact")}
                  aria-label="Quick Form">
                  📝 Quick Form
                </CTAButton>
              </ButtonRow>
            </CTABox>
          </SummarySection>
        </SummaryBody>
      </SummaryCard>
    </SummaryPageContainer>
  );
}
